import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { pb } from "@/lib/pocketbase";
import { RecordModel } from 'pocketbase';

interface BeekeeperSectionProps {
  cooperativeId: string;
}

interface BeekeeperRecord extends RecordModel {
  name: string;
  image: string;
  order: number;
  cooperative: string;
}

interface CooperativeRecord extends RecordModel {
  image: string;
  nepaliName: string;
  membersSpecialName: string;
  slug: string;
}

export const BeekeeperSection: React.FC<BeekeeperSectionProps> = ({ cooperativeId }) => {
  const [searchParams] = useSearchParams();
  const [beekeepers, setBeekeepers] = useState<BeekeeperRecord[]>([]);
  const [cooperative, setCooperative] = useState<CooperativeRecord | null>(null);
  const locale = searchParams.get('locale') || 'en';

  useEffect(() => {
    const loadData = async () => {
      try {
        const beekeepersPromise = pb.collection('members').getList<BeekeeperRecord>(1, 6, {
          filter: `cooperative="${cooperativeId}"`,
          sort: 'order'
        });

        const cooperativePromise = pb.collection('cooperatives').getOne<CooperativeRecord>(cooperativeId);

        const [beekeepersResponse, cooperativeResponse] = await Promise.all([
          beekeepersPromise,
          cooperativePromise,
        ]);

        setBeekeepers(beekeepersResponse.items);
        setCooperative(cooperativeResponse);
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };

    if (cooperativeId) {
      loadData();
    }
  }, [cooperativeId]);

  const getTranslatedContent = (field: 'title' | 'description' | 'viewAllText') => {
    if (locale === 'en') {
      switch (field) {
        case 'title':
          return 'Meet the ' + cooperative?.membersSpecialName || 'Meet our Beekeepers';
        case 'description':
          return cooperative?.nepaliName || 'Supporting local produce';
        case 'viewAllText':
          return 'View All ' + cooperative?.membersSpecialName || 'View all Kisan';
      }
    }
    // Fallback to English content when no translation record exists
    switch (field) {
      case 'title':
        return 'Meet the ' + cooperative?.membersSpecialName || 'Meet our Beekeepers';
      case 'description':
        return cooperative?.nepaliName || 'Supporting local produce';
      case 'viewAllText':
        return 'View All ' + cooperative?.membersSpecialName || 'View all Kisan';
      default:
        return '';
    }
  };

  const getFontClass = () => {
    return locale === 'np' ? 'font-noto' : 'font-slate';
  };

  const currentLocale = searchParams.get('locale') || 'en';

  if (!cooperative) return null;

  return (
    <section className="flex flex-col items-center w-full mt-8">
      <img
      loading="lazy"
      src={pb.files.getURL(cooperative, cooperative.image)}
      className="w-full object-cover h-[200px] sm:h-[300px]"
      alt={cooperative.nepaliName}
      />
      <h2 className={`text-[rgba(0,63,40,1)] text-4xl font-bold mt-8 ${getFontClass()}`}>
      {getTranslatedContent('title')}
      </h2>
      <p className={`text-[rgba(0,63,40,1)] text-sm mt-1 text-center max-w-[280px] mb-8 ${getFontClass()}`}>
      {getTranslatedContent('description')}
      </p>
      <div className="w-full overflow-x-auto no-scrollbar px-4">
      <div className="flex gap-6 pb-4 min-w-max justify-center">
        {beekeepers.length > 0 ? beekeepers.map((beekeeper) => (
        <div
          key={beekeeper.id}
          className="flex flex-col items-center"
        >
          <div className="w-[140px] h-[140px] rounded-full overflow-hidden bg-[rgba(214,255,235,1)]">
          <img
            loading="lazy"
            src={pb.files.getURL(beekeeper, beekeeper.image)}
            alt={`Kisan ${beekeeper.name}`}
            className="w-full h-full object-cover"
          />
          </div>
          <p className={`text-[rgba(0,63,40,1)] text-base font-medium mt-3 text-center ${getFontClass()}`}>
          {beekeeper.name}
          </p>
        </div>
        )) : (
          <p className={`text-[rgba(0,63,40,1)] text-base ${getFontClass()}`}>
            {locale === 'np' ? 'किसानहरू उपलब्ध छैनन्' : 'Farmers not available'}
          </p>
        )}
      </div>
      </div>
      {beekeepers.length > 1 && (
        <Link 
          to={`/sahakari/${cooperative?.slug}`}
          className={`mt-8 px-6 py-2 bg-[rgba(0,63,40,1)] text-white rounded-full text-base hover:bg-[rgba(0,63,40,0.9)] transition-colors ${getFontClass()}`}
        >
          {getTranslatedContent('viewAllText')}
        </Link>
      )}
    </section>
  );
};
